import { CSSProperties, useEffect, useState } from 'react';

/**
 * reveal — shared "staged landing" motion: fade/rise/scale for copy easing
 * in, plus a blur-to-focus treatment for the actual payoff content (e.g. a
 * list of results). Used to give a screen a deliberate, elegant reveal on
 * arrival instead of everything popping in at once.
 *
 * Usage:
 *   <RevealStyles />
 *   <h1 style={reveal('rise', 0.12, 0.7)}>...</h1>
 *   <div style={reveal('fade', 0.32)}>...</div>
 *   {items.map((item, i) => (
 *     <Card key={item.id} style={settled ? undefined : reveal('focus', 0.85 + i * 0.16)} />
 *   ))}
 */
export const REVEAL_EASE = 'cubic-bezier(.25,.46,.45,.94)';

const REVEAL_KEYFRAMES = `
@keyframes sk-reveal-fade{from{opacity:0}to{opacity:1}}
@keyframes sk-reveal-rise{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes sk-reveal-scale{from{opacity:0;transform:scale(.9)}to{opacity:1;transform:scale(1)}}
@keyframes sk-reveal-focus{from{opacity:0;transform:scale(.96) translateY(18px);filter:blur(10px)}to{opacity:1;transform:scale(1) translateY(0);filter:blur(0)}}
`;

/** Injects the shared @keyframes once per screen that uses `reveal()`. */
export function RevealStyles() {
  return <style>{REVEAL_KEYFRAMES}</style>;
}

export type RevealKind = 'fade' | 'rise' | 'scale' | 'focus';

const REVEAL_DEFAULT_DURATION_S: Record<RevealKind, number> = {
  fade: 0.5,
  rise: 0.65,
  scale: 0.5,
  focus: 0.8,
};

/**
 * Returns the inline `animation` style for one element's entrance.
 * `delaySeconds` is when it starts relative to the screen mounting —
 * stagger siblings by spacing out their delays.
 *
 * `direction: 'reverse'` plays the exact same named keyframe backwards —
 * ending at its 'from' state instead of holding at 'to' — for a screen
 * that needs to visibly *undo* its own entrance (e.g. before navigating
 * away) rather than just cutting or fading out some other way.
 */
export function reveal(kind: RevealKind, delaySeconds: number, durationSeconds?: number, direction: 'normal' | 'reverse' = 'normal'): CSSProperties {
  const duration = durationSeconds ?? REVEAL_DEFAULT_DURATION_S[kind];
  const ease = kind === 'fade' ? 'ease' : REVEAL_EASE;
  const dir = direction === 'reverse' ? 'reverse ' : '';
  return { animation: `sk-reveal-${kind} ${duration}s ${ease} ${Math.max(0, delaySeconds).toFixed(2)}s ${dir}both` };
}

/**
 * A `reveal()` animation holds its end state via fill-mode 'both' — which
 * also means it keeps claiming opacity/transform/filter forever after it
 * finishes, silently blocking any later inline style change to those same
 * properties (e.g. a selected/dimmed state). Use this to know when a
 * staggered list's reveal has definitely finished, so callers can stop
 * passing the `reveal()` style and hand those properties back to normal
 * state-driven styling.
 */
export function useRevealSettle(
  itemCount: number,
  { baseDelay = 0, stagger = 0.16, duration = 0.8 }: { baseDelay?: number; stagger?: number; duration?: number } = {}
): boolean {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (itemCount === 0) {
      setSettled(false);
      return;
    }
    const totalMs = (baseDelay + Math.max(0, itemCount - 1) * stagger + duration) * 1000 + 50;
    const t = setTimeout(() => setSettled(true), totalMs);
    return () => clearTimeout(t);
  }, [itemCount, baseDelay, stagger, duration]);

  return settled;
}
